import {
  BookOpen,
  Lightbulb,
  Package,
  Percent,
  Shield,
  ShoppingCart,
  Store,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useInitiativeDialog } from "@/features/tickets";
import { useDropdownHover } from "@/shared/lib/use-dropdown-hover";
import { ROUTES } from "@/shared/model/routes";
import { useSession } from "@/shared/model/session";
import { Button } from "@/shared/ui/kit/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/kit/dropdown-menu";

type NavLink = {
  label: string;
  route: string;
  icon: LucideIcon;
  description: string;
};

export function NavMenu() {
  const { t } = useTranslation();
  const { isAuthenticated } = useSession();
  const initiativeDialog = useInitiativeDialog();
  const buyersLinks: NavLink[] = [
    {
      label: t("nav.howToBuy"),
      route: ROUTES.USER_HOW_TO_BUY,
      icon: ShoppingCart,
      description: t("nav.descriptions.howToBuy"),
    },
    {
      label: t("nav.guarantees"),
      route: ROUTES.USER_GUARANTEES,
      icon: Shield,
      description: t("nav.descriptions.guarantees"),
    },
    {
      label: t("nav.rules"),
      route: ROUTES.USER_RULES,
      icon: BookOpen,
      description: t("nav.descriptions.rules"),
    },
  ];
  const sellersLinks: NavLink[] = [
    {
      label: t("nav.becomeSeller"),
      route: ROUTES.STORE_BECOME_STORE,
      icon: Store,
      description: t("nav.descriptions.becomeSeller"),
    },
    {
      label: t("nav.howToSell"),
      route: ROUTES.STORE_HOW_TO_SELL,
      icon: Package,
      description: t("nav.descriptions.howToSell"),
    },
    {
      label: t("nav.guarantees"),
      route: ROUTES.STORE_GUARANTEES,
      icon: Shield,
      description: t("nav.descriptions.storeGuarantees"),
    },
    {
      label: t("nav.rules"),
      route: ROUTES.STORE_RULES,
      icon: BookOpen,
      description: t("nav.descriptions.storeRules"),
    },
    {
      label: t("nav.commission"),
      route: ROUTES.STORE_COMMISSION,
      icon: Percent,
      description: t("nav.descriptions.commission"),
    },
  ];

  return (
    <nav className="flex items-center gap-4 flex-shrink-0">
      {isAuthenticated && (
        <Button
          variant="link"
          className="nav-link no-underline hover:no-underline !px-0 py-1.5 h-auto text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => initiativeDialog.open()}
        >
          <Lightbulb className="w-4 h-4 text-amber-500" />
          {t("nav.suggestIdea")}
        </Button>
      )}
      <HoverDropdown label={t("nav.buyers")} links={buyersLinks} />
      <HoverDropdown label={t("nav.forStore")} links={sellersLinks} />
    </nav>
  );
}

type HoverDropdownProps = {
  label: string;
  links: NavLink[];
};

const HoverDropdown = ({ label, links }: HoverDropdownProps) => {
  const { open, setOpen, handleMouseEnter, handleMouseLeave } =
    useDropdownHover();

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="link"
          className="nav-link no-underline hover:no-underline px-0 py-1.5 h-auto text-sm font-medium text-muted-foreground hover:text-foreground"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="w-72"
      >
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <DropdownMenuItem key={link.route} asChild>
              <Link
                to={link.route}
                className="flex items-start gap-3 py-2 cursor-pointer"
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{link.label}</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    {link.description}
                  </span>
                </div>
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
